import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../app/store';
import { syncSubscriptionAfterPayment } from '../../../entities/subscription';
import { forgetPendingSubscriptionPayment, isValidPaymentOrderId, readPendingSubscriptionPayment,
    rememberPendingSubscriptionPayment } from '../../../entities/subscription/model/pending-payment';
import { PaymentCheckError, PaymentReturnState, startPaymentStatusPolling } from '../../../entities/subscription/model/poll-payment-status';

export function usePaymentReturn() {
    const dispatch = useAppDispatch();
    const { isAuth, userId } = useAppSelector((state) => state.userAuthSettings);
    const { search } = useLocation();
    const [attempt, setAttempt] = useState(0);
    const [state, setState] = useState<PaymentReturnState>({ phase: 'idle', message: '' });
    const owner = useRef<{ orderId: string; userId: string } | null>(null);
    const retry = useCallback(() => setAttempt((value) => value + 1), []);

    useEffect(() => {
        const params = new URLSearchParams(search);
        const saved = readPendingSubscriptionPayment();
        const orderId = params.get('orderId') ?? saved?.orderId;
        if (!orderId && !params.has('orderId')) {
            setState({ phase: 'idle', message: '' });
            return;
        }
        if (!orderId || !isValidPaymentOrderId(orderId) || params.getAll('orderId').length > 1) {
            setState({ phase: 'error', message: 'Не удалось определить платёж. Проверьте ссылку возврата.' });
            return;
        }
        if (!isAuth || !userId) {
            setState({ phase: 'loginRequired', message: 'Войдите в аккаунт для проверки оплаты.' });
            return;
        }
        const normalizedId = orderId.toLowerCase();
        const knownOwner = owner.current?.orderId === normalizedId ? owner.current.userId
            : saved?.orderId === normalizedId ? saved.userId : userId;
        if (knownOwner !== userId) {
            setState({ phase: 'error', message: 'Войдите в аккаунт, в котором оформляли подписку.' });
            return;
        }
        owner.current = { orderId: normalizedId, userId };
        rememberPendingSubscriptionPayment(normalizedId, userId);
        return startPaymentStatusPolling({
            check: async (signal) => {
                if (document.visibilityState === 'hidden' || navigator.onLine === false) return null;
                const result = await dispatch(syncSubscriptionAfterPayment(normalizedId, { signal }));
                if (syncSubscriptionAfterPayment.fulfilled.match(result)) return result.payload;
                if (result.meta.condition) return null;
                throw new PaymentCheckError(result.payload ?? 'Не удалось проверить оплату.', result.meta.retryable !== false);
            },
            onChange: (next) => {
                if (next.phase === 'succeeded' || next.phase === 'canceled') {
                    forgetPendingSubscriptionPayment(normalizedId, userId);
                }
                setState(next);
            },
        });
    }, [dispatch, isAuth, userId, search, attempt]);

    return { ...state, retry };
}
